import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { packageDealTabSelector, setPackageDealTab } from '@store/slices/portal.slice';
import { RxUtils, Tr } from '@utils';
import { useLoggedinUser, useService } from '@hooks';
import { PackageDealTab } from '@models';
import {
	AdditionalService,
	additionalServiceService,
	notificationService,
	NotificationType,
	PackageDeal,
	packageDealService,
	Permission
} from '@services';
import { Box, Button, Grid, Stack, Tab, Tabs, Tooltip } from '@mui/material';
import { DarkCard, EntityDeleteConfirmationDialog } from '@components/common';
import { EmptyEntityPageLayout, PortalPageTitle } from '@components/portal/common';
import { PackageDealListViewer } from '@components/portal/package-deals';
import { LockIcon } from '@components/icons';
import { ReactComponent as Illustration } from '@assets/illustrations/no-stock-added.svg';

const paddingX = 9;
type Entity = PackageDeal | AdditionalService;

export const PackageDeals: FC = () => {
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const activeTab = useSelector(packageDealTabSelector);
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<Entity[]>([]);
	const [deletingEntities, setDeletingEntities] = useState<Entity[]>([]);
	const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
	const { data: additionalServices = [], trigger: triggerAdditionalServices } = useService(() => additionalServiceService.getAll(), []);
	const { data: packageDeals = [], trigger: triggerPackageDeals } = useService(() => packageDealService.getAll(), []);
	const canAddPackage = Array.isArray(additionalServices) && additionalServices.length >= 2;
	const hasWritePermission = user?.isAdmin() || user?.hasPermission([Permission.SERVICE_WRITE, Permission.PACKAGE_DEAL_WRITE]);
	const displayingEntities = (searchText ? searchResult : activeTab === PackageDealTab.PACKAGES ? packageDeals : additionalServices).sort(
		(item1, item2) => item1.name?.localeCompare(item2.name)
	);

	const onEdit = (d: Entity) => navigate(activeTab === PackageDealTab.PACKAGES ? `./edit-package/${d.id}` : `./edit-service/${d.id}`);
	const onDelete = (entity: Entity) => {
		if (activeTab === PackageDealTab.SERVICES) {
			RxUtils.promisify(additionalServiceService.delete(entity as AdditionalService), () => {
				notificationService.send({
					type: NotificationType.SUCCESS,
					message: t('package-deal.delete-service-success')
				});
				triggerAdditionalServices();
			});
		} else if (activeTab === PackageDealTab.PACKAGES) {
			RxUtils.promisify(packageDealService.delete(entity as PackageDeal), () => {
				notificationService.send({
					type: NotificationType.SUCCESS,
					message: t('package-deal.delete-package-success')
				});
				triggerPackageDeals();
			});
		}
	};
	const onDeleteMultiple = (entities: Entity[]) => {
		if (activeTab === PackageDealTab.SERVICES) {
			RxUtils.promisify(additionalServiceService.deleteMultiple(entities as AdditionalService[]), () => {
				notificationService.send({
					type: NotificationType.SUCCESS,
					message: t('package-deal.delete-service-success')
				});
				triggerAdditionalServices();
			});
		} else if (activeTab === PackageDealTab.PACKAGES) {
			RxUtils.promisify(packageDealService.deleteMultiple(entities as PackageDeal[]), () => {
				notificationService.send({
					type: NotificationType.SUCCESS,
					message: t('package-deal.delete-package-success')
				});
				triggerPackageDeals();
			});
		}
	};
	const onPackageDealSelectionChange = (entity: PackageDeal, selected: boolean) => {
		RxUtils.promisify(packageDealService.setSelected(entity.id, selected), () => {
			notificationService.sendSuccess(t('package-deal.update-package-success'));
			triggerPackageDeals();
		});
	};

	useEffect(() => {
		if (searchText) {
			if (activeTab === PackageDealTab.SERVICES) {
				RxUtils.promisify(additionalServiceService.search(searchText), setSearchResult);
			} else if (activeTab === PackageDealTab.PACKAGES) {
				RxUtils.promisify(packageDealService.search(searchText), setSearchResult);
			}
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	useEffect(() => (setSearchText(''), setDeletingEntities([]), setSelectedEntities([])), [activeTab]);

	return (
		<Stack direction="column" spacing={4} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5, overflow: 'hidden' }}>
			<PortalPageTitle
				paddingX={paddingX}
				title={<Tr.Portal path="package-deal.title" />}
				subtitle={<Tr.Portal path="package-deal.subtitle" />}
				displaySearchbox
				searchboxProps={{
					testId: 'package-deals-search-box',
					placeholder:
						activeTab === PackageDealTab.PACKAGES
							? t('package-deal.search-placeholder-packages')
							: t('package-deal.search-placeholder-services'),
					searchText,
					onSearchTextChange: setSearchText
				}}
			/>
			{hasWritePermission && (
				<Box>
					<Grid container direction="row" sx={{ paddingX }} columnSpacing={5}>
						<Grid item xs={6}>
							<DarkCard
								sx={{ flexGrow: 1 }}
								pattern="linepattern1"
								title={<Tr.Portal path="package-deal.add-service-title" />}
								subtitle={<Tr.Portal path="package-deal.add-service-subtitle" />}
								onClick={() => navigate('./add-service')}
							/>
						</Grid>
						<Grid item xs={6}>
							<DarkCard
								sx={{ flexGrow: 1 }}
								pattern="linepattern2"
								title={
									<Stack direction="row" alignItems="center" spacing={1}>
										<Box>
											<Tr.Portal path="package-deal.add-package-title" />
										</Box>
										{!canAddPackage ? (
											<Tooltip
												title={<Tr.Portal path="package-deal.package-disabled-tooltip" />}
												arrow
												placement="bottom"
											>
												<LockIcon sx={{ width: 22, height: 22, color: 'common.white' }} />
											</Tooltip>
										) : null}
									</Stack>
								}
								subtitle={<Tr.Portal path="package-deal.add-package-subtitle" />}
								onClick={canAddPackage ? () => navigate('./add-package') : undefined}
							/>
						</Grid>
					</Grid>
				</Box>
			)}
			<Stack direction="column" sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
				{additionalServices?.length || packageDeals?.length ? (
					<Tabs value={activeTab} onChange={(_e, val) => dispatch(setPackageDealTab(val as PackageDealTab))} centered>
						<Tab value={PackageDealTab.SERVICES} label={<Tr.Portal path="package-deal.tab-services" />} />
						<Tab value={PackageDealTab.PACKAGES} label={<Tr.Portal path="package-deal.tab-packages" />} />
					</Tabs>
				) : null}

				{(Array.isArray(additionalServices) && additionalServices.length) ||
				(Array.isArray(packageDeals) && packageDeals.length) ? (
					<Stack direction="column" spacing={3} sx={{ flexGrow: 1, maxHeight: 1.0, overflow: 'hidden' }}>
						<PackageDealListViewer
							entityType={activeTab === PackageDealTab.PACKAGES ? 'package' : 'service'}
							showResultCount={!!searchText}
							paddingX={paddingX}
							entities={displayingEntities}
							selected={selectedEntities}
							empty={<Illustration />}
							onSelectChange={setSelectedEntities}
							onEdit={onEdit}
							onDelete={(entity) => setDeletingEntities([entity])}
							onPackageDealSelectionChange={onPackageDealSelectionChange}
						/>
						{selectedEntities.length ? (
							<Stack direction="row" spacing={3} justifyContent="center" sx={{ paddingX }}>
								<Button
									data-testid="delete-package-deal-modal-open-button"
									variant="contained"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setDeletingEntities(selectedEntities)}
								>
									<Tr.Portal path="delete" />
								</Button>
								<Button
									data-testid="cancel-package-deal-selection-button"
									variant="outlined"
									color="secondary"
									sx={{ textTransform: 'uppercase' }}
									onClick={() => setSelectedEntities([])}
								>
									<Tr.Portal path="cancel" />
								</Button>
							</Stack>
						) : null}
					</Stack>
				) : (
					<EmptyEntityPageLayout
						illustration={<Illustration />}
						title={<Tr.Portal path="package-deal.no-package-deals-title" />}
					/>
				)}
			</Stack>
			<EntityDeleteConfirmationDialog
				open={!!deletingEntities?.length}
				content={
					<Tr.Portal
						path={
							activeTab === PackageDealTab.PACKAGES
								? 'package-deal.delete-package-confirmation-message'
								: 'package-deal.delete-service-confirmation-message'
						}
					/>
				}
				buttonProps={{ 'data-testid': 'package-deal-delete-confirmation-button' }}
				onClose={() => setDeletingEntities([])}
				onAccept={() => {
					deletingEntities.length > 1 ? onDeleteMultiple?.(deletingEntities) : onDelete?.(deletingEntities[0]);
					setDeletingEntities([]);
					setSelectedEntities([]);
				}}
			>
				{deletingEntities.map((entity) => entity.name).join(', ')}
			</EntityDeleteConfirmationDialog>
		</Stack>
	);
};
